import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Roles } from '../constants/roles';
import { getStoredUser } from '../utils/authStorage';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    reloading: boolean;
}

// A chunk request that 404s after a deploy (old tab, new release) rejects the
// lazy import with ChunkLoadError — for both JS chunks and mini-css-extract
// CSS chunks. React.lazy caches the rejection, so the only real recovery is a
// full reload against the fresh index.html.
const CHUNK_RELOAD_KEY = 'comtev:chunk-reload-at';
const CHUNK_RELOAD_WINDOW_MS = 60_000;

export function isChunkLoadError(error: Error): boolean {
    return (
        error.name === 'ChunkLoadError' ||
        /Loading (CSS )?chunk .+ failed/i.test(error.message)
    );
}

function shouldAutoReload(): boolean {
    try {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
        return !last || Date.now() - last > CHUNK_RELOAD_WINDOW_MS;
    } catch {
        // Storage unavailable (e.g. blocked) — reloading without a guard
        // risks a loop, so fall through to the manual-refresh UI instead.
        return false;
    }
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        reloading: false,
    };

    public static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        if (isChunkLoadError(error) && shouldAutoReload()) {
            sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
            this.setState({ reloading: true });
            window.location.reload();
        }
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    private isChunkError(): boolean {
        return this.state.error !== null && isChunkLoadError(this.state.error);
    }

    private handleGoHome = () => {
        let homePath = '/dashboard';
        const { user } = getStoredUser();
        if (user?.role === Roles.superAdmin) {
            homePath = '/admin/promos';
        }
        window.location.href = homePath;
    };

    public render() {
        if (this.state.hasError) {
            // A reload is already in flight — don't flash the error card.
            if (this.state.reloading) {
                return null;
            }

            if (this.props.fallback) {
                return this.props.fallback;
            }

            const chunkError = this.isChunkError();

            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '40px',
                    textAlign: 'center',
                    backgroundColor: 'var(--soft-bg)',
                }}>
                    <div style={{
                        backgroundColor: 'var(--card-bg)',
                        padding: '40px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        maxWidth: '500px',
                        border: '1px solid var(--border-color)',
                    }}>
                        <div style={{ fontSize: '4rem', marginBottom: '20px' }}>😕</div>
                        <h1 style={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: 'var(--heading-color)',
                            marginBottom: '12px',
                        }}>
                            Something went wrong
                        </h1>
                        <p style={{
                            color: 'var(--text-light)',
                            marginBottom: '24px',
                            fontSize: '0.95rem',
                        }}>
                            {"We're sorry, but something unexpected happened. Please try refreshing the page."}
                        </p>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <pre style={{
                                backgroundColor: '#fef2f2',
                                padding: '12px',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                color: '#991b1b',
                                textAlign: 'left',
                                overflow: 'auto',
                                marginBottom: '20px',
                                maxHeight: '150px',
                            }}>
                                {this.state.error.toString()}
                            </pre>
                        )}
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button
                                onClick={chunkError ? this.handleGoHome : this.handleReset}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: '#f7fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    color: '#4a5568',
                                }}
                            >
                                {chunkError ? 'Go Home' : 'Try Again'}
                            </button>
                            <button
                                onClick={chunkError ? this.handleReload : this.handleGoHome}
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: '#f27141',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    color: 'white',
                                }}
                            >
                                {chunkError ? 'Refresh Page' : 'Go Home'}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
